// Microbe RTS — per-tick economy: harvest income, trickle, photosynthesis, storage caps,
// and the toxin resource economy. Amino is the scarce gate; storage scales with territory.
// (The ambient toxin unit-DoT was removed so opening drones can drop anywhere, incl. deep water.)
//
// Each side's bot profile scales its income (via ctx.profiles[side].incomeMult), applied here so
// rates() reports the true post-scaling income for the HUD.

import type { GameState, Side, RateSummary } from '../types.js';
import {
  NODE_SPECS, HOME_TRICKLE, NODE_TRICKLE, ECON,
} from '../config.js';
import type { SimCtx } from './world.js';
import { ownedCapturable, countOwnedType, clampNum } from './world.js';
import { photoMult, hasToxinResist, energyMultOf, aminoMultOf } from './units.js';

/** Income per second for a side (used both for HUD rates and to accrue resources). */
export function computeRates(state: GameState, ctx: SimCtx, side: Side): RateSummary {
  const col = state.colonies[side];
  const pMult = photoMult(col);
  const resist = hasToxinResist(col);

  let energy = 0;
  let amino = 0;
  let toxin = 0;

  for (const n of state.nodes) {
    if (n.owner !== side) continue;

    if (n.type === 'home') {
      energy += HOME_TRICKLE.energy;
      amino += HOME_TRICKLE.amino;
      continue;
    }

    // Anti-stall trickle on every owned node.
    energy += NODE_TRICKLE.energy;
    amino += NODE_TRICKLE.amino;

    const spec = NODE_SPECS[n.type];

    // AUTO-WORKED: an owned node produces as if staffed to its cap, scaled by autoWorkEfficiency.
    // No units needed — owning territory IS the economy (workers were removed).
    if (spec.needsPhoto && pMult <= 0) continue; // light shelf worthless without photosynthesis
    if (spec.requiresResist && !resist) continue; // toxin seep needs resistance to mine

    const slots = spec.workerCap * ECON.autoWorkEfficiency;
    let ePer = spec.energyPerWorker;
    if (spec.needsPhoto) ePer *= pMult; // light shelves scale with photosynthesis level
    energy += ePer * slots;
    amino += spec.aminoPerWorker * slots;
    toxin += spec.toxinPerWorker * slots;
  }

  // Income scales per-side by that side's bot profile (null = human → no scaling). This makes the
  // sandbox's two bots each honor their own profile instead of both borrowing one difficulty.
  const mult = ctx.profiles[side]?.incomeMult ?? 1;
  // Genome economy boosts (energy/amino income multipliers; default 1 when un-teched).
  const eMult = energyMultOf(col);
  const aMult = aminoMultOf(col);
  return { energy: energy * mult * eMult, amino: amino * mult * aMult, toxin: toxin * mult };
}

function storageCaps(state: GameState, side: Side): RateSummary {
  const nodes = ownedCapturable(state, side).length;
  const toxNodes = countOwnedType(state, side, 'toxin');
  return {
    energy: ECON.energyCapBase + ECON.energyCapPerNode * nodes,
    amino: ECON.aminoCapBase + ECON.aminoCapPerNode * nodes,
    toxin: ECON.toxinCapBase + ECON.toxinCapPerToxinNode * toxNodes,
  };
}

/** Accrue income and clamp to storage caps. */
export function applyEconomy(state: GameState, ctx: SimCtx, dt: number): void {
  for (const side of ['you', 'bot'] as Side[]) {
    const col = state.colonies[side];
    const r = computeRates(state, ctx, side);
    col.res.energy += r.energy * dt;
    col.res.amino += r.amino * dt;
    col.res.toxin += r.toxin * dt;

    const caps = storageCaps(state, side);
    col.res.energy = clampNum(col.res.energy, 0, caps.energy);
    col.res.amino = clampNum(col.res.amino, 0, caps.amino);
    col.res.toxin = clampNum(col.res.toxin, 0, caps.toxin);
  }

  // (Ambient toxin unit-debuff REMOVED: opening drones now drop anywhere — including the deep/abyssal
  //  bands — so a passive DoT there would drown a lone spawn before it found a crater. The deep bands
  //  keep their visual toxin tint + toxin-seep economy; they just no longer chip unit hp.)
}
